import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Flex } from '@qga/roo-ui/components';
import CheckAvailability from 'components/PropertyPage/PropertyAvailability/RoomTypes/RoomTypeList/CheckAvailability';
import TermsAndConditionsModal from '../CheckAvailability/components/TermsAndConditionsModal';
import UnavailableRoomType from 'components/PropertyPage/PropertyAvailability/RoomTypes/RoomTypeList/UnavailableRoomType';
import RoomTypeDetailsGallery from './components/RoomTypeDetailsGallery';
import { RoomTypeWrapper, OffersWrapper, CheckAvailabilityList } from './components/primitives';
import { getAllAvailableOffers, getExclusiveOffer } from 'store/exclusiveOffer/exclusiveOfferSelectors';
import { sortBy, keys, partition, isEmpty } from 'lodash';
import { useBreakpoints } from 'hooks/useBreakpoints';
import Offer from '../Offer';
import * as sessionStorage from 'lib/browser/sessionStorage';
import { Offer as OfferType } from 'types/property';
import { captureErrorInSentry } from 'lib/errors';

interface RoomOfferProps extends OfferType {
  offerTotal?: { amount?: string };
}

interface OffersListProps {
  roomName: string;
  offers: OfferType[];
  roomTypeId: string;
  smallTablet?: boolean;
  exclusiveOffer?: boolean;
}

const OffersList = ({ roomName, offers, roomTypeId, exclusiveOffer = false }: OffersListProps) => {
  const totalOffers = offers.length;
  const { isLessThanBreakpoint } = useBreakpoints();
  const isMobile = isLessThanBreakpoint(0);

  return (
    <OffersWrapper
      mt={[0, 0, 10]}
      pt={[0, 3]}
      border={isMobile ? 2 : 0}
      borderColor="greys.porcelain"
      exclusiveOffer={exclusiveOffer}
      data-testid="offer-wrapper"
    >
      {offers.map((offer, counter) => (
        <Offer
          roomTypeId={roomTypeId}
          offer={offer}
          counter={counter + 1}
          key={offer.id}
          roomName={roomName}
          mb={4}
          totalOffers={totalOffers}
          data-testid="offer"
        />
      ))}
    </OffersWrapper>
  );
};

interface RoomTypeProps {
  roomType: {
    id: string;
    offerId?: string;
    name: string;
    description?: string;
    roomTypeFacilities: string[];
    offers?: OfferType[];
    exclusiveOffers?: RoomOfferProps[];
    maxOccupantCount?: number;
    images?: { urlMedium: string; caption?: string }[];
    mainImage?: { urlMedium: string; caption?: string };
    title?: string;
    termsAndConditions?: string;
  };
  hasQuery?: boolean;
}

const RoomType = ({ roomType, hasQuery = false }: RoomTypeProps) => {
  let unavailableOfferIds: string[] = [];

  try {
    unavailableOfferIds = sessionStorage.get('unavailableOfferIds') || [];
  } catch (error) {
    captureErrorInSentry(error);
  }
  const { exclusiveOffers, id, name, offers, termsAndConditions } = roomType;
  const filteredOffers = offers?.filter((offer) => !unavailableOfferIds.includes(offer.id)) || [];

  const hasOffers = !!(filteredOffers && filteredOffers.length);
  const hasExclusiveOffers = !!exclusiveOffers?.length;
  const isUnavailable = hasQuery && !hasOffers;
  const exclusiveOffer = useSelector(getExclusiveOffer);
  const isExclusiveOffer = !isEmpty(exclusiveOffer);

  const { isLessThanBreakpoint } = useBreakpoints();
  const isMobile = isLessThanBreakpoint(0);
  const availableOffers = useSelector(getAllAvailableOffers);

  const [available, unavailable] = partition(exclusiveOffers, (exclusiveOffer) => keys(availableOffers).includes(exclusiveOffer.id));
  const sortedAvailable = sortBy(available, (exclusiveOffer) => parseInt(availableOffers[exclusiveOffer.id]?.charges.total.amount ?? '0'));
  const sortedUnavailable = sortBy(unavailable, (exclusiveOffer) => parseInt(exclusiveOffer.offerTotal?.amount ?? '0'));
  const sortedExclusiveOffers = [...sortedAvailable, ...sortedUnavailable];

  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const smallTablet = windowWidth > 750 && windowWidth < 1020;
  const weirdLaptopSizes = windowWidth >= 1100 && windowWidth < 1257;

  if (isUnavailable) {
    return (
      <UnavailableRoomType
        roomType={{
          ...roomType,
          description: roomType.description || '',
        }}
        hasQuery={hasQuery}
      />
    );
  }

  return (
    <Box mb={[0, 4, 8]}>
      <RoomTypeWrapper
        isMobile={isMobile}
        smallTablet={smallTablet}
        exclusiveOffer={isExclusiveOffer}
        pl={isExclusiveOffer ? [0, 0, 10] : [0, 0, 6]}
        pr={isExclusiveOffer ? 0 : [0, 0, 6]}
        pb={[2, 0]}
        pt={isExclusiveOffer ? [0, 2, 8] : [0, 6]}
      >
        <Flex width={['100%', '100%', '321px']} flexDirection={'column'}>
          <RoomTypeDetailsGallery roomType={roomType} hasOffers={hasOffers} smallTablet={smallTablet} exclusiveOffer={isExclusiveOffer} />
        </Flex>
        <Flex
          mx={smallTablet || isExclusiveOffer ? 0 : [0, 8]}
          width={['100%', '100%', weirdLaptopSizes ? `${windowWidth - 460}px` : '800px']}
          alignItems="flex-start"
          px={[6, 0]}
        >
          {hasOffers && (
            <OffersList
              roomName={name}
              offers={filteredOffers}
              roomTypeId={id}
              smallTablet={smallTablet}
              exclusiveOffer={isExclusiveOffer}
            />
          )}

          {hasExclusiveOffers && (
            <CheckAvailabilityList data-testid="check-availability-list">
              {sortedExclusiveOffers.map((exclusiveOffer, i) => (
                <CheckAvailability key={exclusiveOffer.id} room={roomType} offer={exclusiveOffer} index={i} />
              ))}
            </CheckAvailabilityList>
          )}
          {!hasExclusiveOffers && !hasQuery && <CheckAvailability room={roomType} />}
        </Flex>
      </RoomTypeWrapper>
      {termsAndConditions && <TermsAndConditionsModal roomName={name} termsAndConditions={termsAndConditions} />}
    </Box>
  );
};

export default memo(RoomType);
